import {
  Pressable,
  StyleSheet,
  Text,
  View,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";
import React from "react";
import { Entypo, MaterialIcons } from "@expo/vector-icons";
import { Units } from "../../constants/Unit";
import { EDColors } from "../../constants/Colors";
import { GradientCircularProgress } from "react-native-circular-gradient-progress";
import PieChart from "../../components/PieChart";
import { renderLevel } from "../../constants/Config";
import { formatNumberWithCommas } from "../../utils/helper";

const SelectableText = (props) => <Text selectable {...props} />;

const CHOICE_COLORS = {
  A: "#0243DD",
  B: "#15B86B",
  C: "#E67E22",
  D: "#9B59B6",
};

function formatRelativeTime(value) {
  if (value == null) return "—";
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? "s" : ""} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? "s" : ""} ago`;
  if (diffWeek < 4) return `${diffWeek} week${diffWeek !== 1 ? "s" : ""} ago`;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function getLevelName(levelId) {
  if (levelId == null) return "—";
  const level = renderLevel.find((l) => l.LevelId === levelId);
  return level?.Name ?? "—";
}

function ExpandedDetail({ questionDetail, sectionDetail, loadingDetail, aggregate }) {
  const { width } = useWindowDimensions();
  const isNarrow = width < 600;
  const agg = aggregate || {};
  const levelName = getLevelName(agg.LevelID ?? agg.LevelId);
  const sectionName = sectionDetail?.Name ?? (agg.SectionID ?? agg.SectionId) ?? null;

  const correctAnswer = (questionDetail && (questionDetail.Correctanswer ?? questionDetail.CorrectAnswer)) || "";
  const choices = [
    { letter: "A", text: questionDetail?.AnswerA ?? "—", value: agg.NumTimesAnsA || 0 },
    { letter: "B", text: questionDetail?.AnswerB ?? "—", value: agg.NumTimesAnsB || 0 },
    { letter: "C", text: questionDetail?.AnswerC ?? "—", value: agg.NumTimesAnsC || 0 },
    { letter: "D", text: questionDetail?.AnswerD ?? "—", value: agg.NumTimesAnsD || 0 },
  ];
  const totalByChoices = choices.reduce((sum, c) => sum + c.value, 0);
  const correctByChoices = correctAnswer
    ? (choices.find((c) => c.letter === correctAnswer)?.value ?? 0)
    : 0;
  const wrongByChoices = totalByChoices - correctByChoices;
  const pieData = [
    { label: "A", value: agg.NumTimesAnsA || 0, color: CHOICE_COLORS.A },
    { label: "B", value: agg.NumTimesAnsB || 0, color: CHOICE_COLORS.B },
    { label: "C", value: agg.NumTimesAnsC || 0, color: CHOICE_COLORS.C },
    { label: "D", value: agg.NumTimesAnsD || 0, color: CHOICE_COLORS.D },
  ];

  return (
    <View style={[styles.expandedRow, isNarrow && styles.expandedRowNarrow]}>
      <View style={styles.expandedHeader}>
        <SelectableText style={styles.expandedTitle}>{levelName}</SelectableText>
        {sectionName != null && (
          <SelectableText style={styles.expandedSubtitle}>{sectionName}</SelectableText>
        )}
      </View>
      <View style={[styles.twoCol, isNarrow && styles.twoColNarrow]}>
        <View style={[styles.col1, isNarrow && styles.col1Narrow]}>
          {loadingDetail ? (
            <View style={styles.loadingBox}>
              <ActivityIndicator size="small" color={EDColors.primary} />
              <SelectableText style={styles.loadingText}>Loading question…</SelectableText>
            </View>
          ) : !questionDetail ? (
            <SelectableText style={styles.notFoundText}>Question not found</SelectableText>
          ) : (
            <>
              <SelectableText style={styles.qnoLabel}>QNo: {questionDetail.QNo || "—"}</SelectableText>
              <SelectableText style={styles.questionText}>{questionDetail.Question || "—"}</SelectableText>
              <View style={styles.choicesBlock}>
                {choices.map((c) => {
                  const isCorrect = c.letter === correctAnswer;
                  const pctNum = totalByChoices > 0 ? (c.value / totalByChoices) * 100 : 0;
                  const pctDisplay = totalByChoices > 0 ? ((c.value / totalByChoices) * 100).toFixed(1) : "0.0";
                  const barColor = CHOICE_COLORS[c.letter];
                  return (
                    <View
                      key={c.letter}
                      style={[
                        styles.choiceRow,
                        isCorrect ? styles.choiceCorrect : styles.choiceIncorrect,
                      ]}
                    >
                      <SelectableText style={styles.choiceLetter}>{c.letter}.</SelectableText>
                      <SelectableText style={styles.choiceText}>{c.text}</SelectableText>
                      <View style={styles.choiceBarWrap}>
                        <View style={styles.choiceBarTrack}>
                          <View
                            style={[
                              styles.choiceBarFill,
                              { width: `${Math.min(100, pctNum)}%`, backgroundColor: barColor },
                            ]}
                          />
                        </View>
                        <SelectableText style={styles.choicePct}>{pctDisplay}%</SelectableText>
                      </View>
                      {isCorrect ? (
                        <MaterialIcons name="check-circle" size={20} color={EDColors.green} />
                      ) : (
                        <MaterialIcons name="cancel" size={20} color={EDColors.red} />
                      )}
                    </View>
                  );
                })}
              </View>
            </>
          )}
        </View>
        <View style={styles.col2}>
          <SelectableText style={styles.pieTitle}>Attempts by choice</SelectableText>
          <View style={styles.pieWrap}>
            <PieChart data={pieData} size={140} showTooltip />
          </View>
          <View style={styles.pieLegend}>
            {pieData.map((d) => (
              <View key={d.label} style={styles.pieLegendItem}>
                <View style={[styles.pieLegendSwatch, { backgroundColor: d.color }]} />
                <SelectableText style={[styles.pieLegendLabel, { marginLeft: 4 }]}>
                  {d.label}: {formatNumberWithCommas(d.value)}
                </SelectableText>
              </View>
            ))}
          </View>
          <View style={styles.totalsBlock}>
            <SelectableText style={styles.totalsTitle}>Totals</SelectableText>
            <SelectableText style={styles.totalsRow}>&bull; Attempts: <SelectableText style={styles.totalsNum}>{formatNumberWithCommas(totalByChoices)}</SelectableText></SelectableText>
            <SelectableText style={styles.totalsRow}>&bull; Correct: <SelectableText style={[styles.totalsNum, { color: EDColors.green }]}>{formatNumberWithCommas(correctByChoices)}</SelectableText></SelectableText>
            <SelectableText style={styles.totalsRow}>&bull; Wrong: <SelectableText style={[styles.totalsNum, { color: EDColors.red }]}>{formatNumberWithCommas(wrongByChoices)}</SelectableText></SelectableText>
          </View>
          <SelectableText style={styles.lastUpdatedText}>
            Last updated: {formatRelativeTime(agg.LastProcessed)}
          </SelectableText>
        </View>
      </View>
    </View>
  );
}

const Quescomp = ({
  levelId,
  Qno,
  fill,
  total,
  pass,
  failed,
  NCVECLesson,
  NCVECTopic,
  SectionID,
  byCustom,
  byTopic,
  customLessonID,
  expanded,
  onPress,
  questionDetail,
  sectionDetail,
  loadingDetail,
  aggregate,
}) => {
  return (
    <View style={styles.wrapper}>
      <View style={styles.container}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <View>
            <SelectableText style={styles.labelText}>{NCVECTopic ? "NCVEC Section" : NCVECLesson ? "NCVEC Lesson" : NCVECTopic ? "NCVEC Section" : byCustom ? "LevelID" : "QNO"}</SelectableText>
            <SelectableText style={styles.labelText}>{byTopic ? "TopicID" : byCustom ? "LessonID" : "LevelID"}</SelectableText>
          </View>
          <View style={{ marginLeft: 20 }}>
            <SelectableText style={styles.labelText}>:{byCustom ? levelId : NCVECLesson ? SectionID : NCVECTopic ? Qno?.slice(0, 3) : Qno}</SelectableText>
            <SelectableText style={styles.labelText}>:{byCustom ? customLessonID : levelId}</SelectableText>
          </View>
        </View>
        <View style={styles.vDivider} />
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <View style={{ marginRight: 20 }}>
            <SelectableText style={styles.labelTextDescriptive}>Percent</SelectableText>
            <SelectableText style={styles.labelTextDescriptive}>Correct: </SelectableText>
          </View>
          <GradientCircularProgress
            size={54}
            startColor={EDColors.gradient[2]}
            middleColor={EDColors.gradient[1]}
            endColor={EDColors.gradient[0]}
            progress={fill}
            emptyColor={EDColors.lightgrey}
            strokeWidth={4}
            children={
              <View style={[StyleSheet.absoluteFillObject, styles.progressCenter]}>
                <SelectableText style={styles.progressText}>{fill}%</SelectableText>
              </View>
            }
          />
        </View>
        <View style={styles.vDivider} />
        <View style={styles.statsRow}>
          <View style={styles.statBlock}>
            <SelectableText style={styles.labelTextDescriptive}>Number of Attempts:</SelectableText>
            <SelectableText style={styles.statNum}>{total}</SelectableText>
          </View>
          <View style={styles.statBlock}>
            <SelectableText style={styles.labelTextDescriptive}>Times Correct:</SelectableText>
            <SelectableText style={[styles.statNum, { color: "#15B86B" }]}>{pass}</SelectableText>
          </View>
          <View style={styles.statBlock}>
            <SelectableText style={styles.labelTextDescriptive}>Times Missed:</SelectableText>
            <SelectableText style={[styles.statNum, { color: "#BF1D27" }]}>{failed}</SelectableText>
          </View>
        </View>
        <Pressable
          style={styles.chevronButton}
          onPress={onPress != null ? onPress : undefined}
          disabled={onPress == null}
        >
          <View style={[styles.vDivider, { marginRight: 17 }]} />
          <Entypo
            name={expanded ? "chevron-up" : "chevron-down"}
            size={24}
            color="black"
          />
        </Pressable>
      </View>
      {onPress != null && expanded && (
        <ExpandedDetail
          questionDetail={questionDetail}
          sectionDetail={sectionDetail}
          loadingDetail={loadingDetail}
          aggregate={aggregate}
        />
      )}
    </View>
  );
};

export default Quescomp;

const styles = StyleSheet.create({
  wrapper: {
    marginHorizontal: 17,
    marginTop: 10,
  },
  container: {
    borderWidth: 1,
    borderColor: "#eee",
    borderStyle: "solid",
    paddingVertical: 10,
    paddingHorizontal: 17,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
    elevation: 2,
    borderRadius: 12,
  },
  labelText: {
    color: "#0243DD",
    fontFamily: Units.ftFamily.MontserratR,
  },
  labelTextDescriptive: {
    fontFamily: Units.ftFamily.MontserratR,
  },
  vDivider: {
    height: "100%",
    minHeight: 36,
    width: 1,
    backgroundColor: "#eee",
  },
  statsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  statBlock: {
    alignItems: "center",
    marginHorizontal: 10,
  },
  statNum: {
    color: "#0243DD",
    fontFamily: Units.ftFamily.MontserratR,
  },
  progressCenter: {
    alignItems: "center",
    justifyContent: "center",
  },
  progressText: {
    fontFamily: Units.ftFamily.KarlaR,
    color: EDColors.topbar,
    textAlign: "center",
  },
  chevronButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 40,
  },
  expandedRow: {
    borderWidth: 1,
    borderColor: "#eee",
    borderTopWidth: 0,
    borderStyle: "solid",
    paddingVertical: 16,
    paddingHorizontal: 17,
    backgroundColor: EDColors.weeklightgrey,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  expandedRowNarrow: {
    paddingHorizontal: 12,
  },
  twoCol: {
    flexDirection: "row",
  },
  twoColNarrow: {
    flexDirection: "column",
  },
  col1: {
    flex: 1,
    minWidth: 0,
    marginRight: 24,
    justifyContent: "center",
  },
  col1Narrow: {
    marginRight: 0,
    marginBottom: 16,
  },
  col2: {
    flex: 1,
    minWidth: 0,
    alignItems: "center",
  },
  loadingBox: {
    alignItems: "center",
    paddingVertical: 24,
  },
  loadingText: {
    marginTop: 8,
    fontFamily: Units.ftFamily.MontserratR,
    color: EDColors.topbar,
  },
  notFoundText: {
    fontFamily: Units.ftFamily.MontserratR,
    color: EDColors.red,
    marginBottom: 12,
  },
  expandedHeader: {
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  expandedTitle: {
    fontFamily: Units.ftFamily.MontserratB,
    color: EDColors.topbar,
    fontSize: 16,
    textAlign: "center",
  },
  expandedSubtitle: {
    fontFamily: Units.ftFamily.MontserratR,
    color: EDColors.topbar,
    fontSize: 13,
    marginTop: 4,
    textAlign: "center",
    opacity: 0.9,
  },
  qnoLabel: {
    fontFamily: Units.ftFamily.MontserratB,
    color: EDColors.topbar,
    marginBottom: 6,
    fontSize: 15,
  },
  questionText: {
    fontFamily: Units.ftFamily.MontserratR,
    color: EDColors.topbar,
    marginBottom: 12,
    fontSize: 14,
  },
  choicesBlock: {
    marginBottom: 10,
    alignSelf: "stretch",
  },
  choiceRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  choiceCorrect: {
    backgroundColor: EDColors.bggreen,
    borderLeftWidth: 3,
    borderLeftColor: EDColors.green,
  },
  choiceIncorrect: {
    backgroundColor: EDColors.bgred,
    borderLeftWidth: 3,
    borderLeftColor: EDColors.red,
  },
  choiceLetter: {
    fontFamily: Units.ftFamily.MontserratB,
    marginRight: 6,
    color: EDColors.topbar,
  },
  choiceText: {
    flex: 1,
    fontFamily: Units.ftFamily.MontserratR,
    color: EDColors.topbar,
    fontSize: 13,
  },
  choiceBarWrap: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 8,
    minWidth: 120,
  },
  choiceBarTrack: {
    flex: 1,
    height: 10,
    backgroundColor: EDColors.lightgrey,
    borderRadius: 5,
    overflow: "hidden",
    marginRight: 8,
  },
  choiceBarFill: {
    height: "100%",
    borderRadius: 5,
  },
  choicePct: {
    fontFamily: Units.ftFamily.MontserratB,
    fontSize: 13,
    color: EDColors.topbar,
    minWidth: 40,
    textAlign: "right",
  },
  pieTitle: {
    fontFamily: Units.ftFamily.MontserratB,
    color: EDColors.topbar,
    marginBottom: 8,
    fontSize: 14,
  },
  pieWrap: {
    alignItems: "center",
    marginBottom: 8,
  },
  pieLegend: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    marginBottom: 16,
  },
  pieLegendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 12,
    marginBottom: 4,
  },
  pieLegendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
  pieLegendLabel: {
    fontFamily: Units.ftFamily.MontserratR,
    fontSize: 12,
    color: EDColors.topbar,
  },
  totalsBlock: {
    alignSelf: "stretch",
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#eee",
  },
  totalsTitle: {
    fontFamily: Units.ftFamily.MontserratB,
    color: EDColors.topbar,
    marginBottom: 6,
    fontSize: 13,
  },
  totalsRow: {
    fontFamily: Units.ftFamily.MontserratR,
    fontSize: 13,
    color: EDColors.topbar,
    marginBottom: 2,
    marginLeft: 10
  },
  totalsNum: {
    fontFamily: Units.ftFamily.MontserratB,
    color: "#0243DD",
  },
  lastUpdatedText: {
    fontFamily: Units.ftFamily.MontserratR,
    fontSize: 11,
    color: EDColors.grey,
    marginTop: 10,
    textAlign: "center",
  },
});
